import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useStore from '../store/useStore';
import AuthModal from '../components/AuthModal';
import { useT } from '../i18n';
import Logo from '../components/Logo';

function FeatureIcon({ name }) {
  const common = { width: 16, height: 16, viewBox: '0 0 16 16', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'rocket':
      return (
        <svg {...common}><path d="M11 2c-3 0-6 3-7 5l3 3c2-1 5-4 5-7l-1-1z" /><path d="M5 9l-2 2 2 2 2-2" /><circle cx="9.5" cy="6.5" r="1" /></svg>
      );
    case 'lock':
      return (
        <svg {...common}><rect x="3" y="7" width="10" height="7" rx="1.5" /><path d="M5.5 7V5a2.5 2.5 0 0 1 5 0v2" /></svg>
      );
    case 'globe':
      return (
        <svg {...common}><circle cx="8" cy="8" r="6" /><path d="M2 8h12M8 2c2 2 2 10 0 12M8 2c-2 2-2 10 0 12" /></svg>
      );
    case 'database':
      return (
        <svg {...common}><ellipse cx="8" cy="3.5" rx="5" ry="1.8" /><path d="M3 3.5v9c0 1 2.2 1.8 5 1.8s5-.8 5-1.8v-9" /><path d="M3 8c0 1 2.2 1.8 5 1.8s5-.8 5-1.8" /></svg>
      );
    case 'backup':
      return (
        <svg {...common}><path d="M8 3v6m-3-3 3 3 3-3" /><path d="M3 12.5h10" /><circle cx="3" cy="12.5" r="0.4" fill="currentColor" /></svg>
      );
    case 'pulse':
      return (
        <svg {...common}><path d="M2 8h3l1.5-3 3 6L11 8h3" /></svg>
      );
    case 'sync':
      return (
        <svg {...common}><path d="M3 8a5 5 0 0 1 8.5-3.5L13 6" /><path d="M13 3v3h-3" /><path d="M13 8a5 5 0 0 1-8.5 3.5L3 10" /><path d="M3 13v-3h3" /></svg>
      );
    case 'mail':
      return (
        <svg {...common}><rect x="2" y="3.5" width="12" height="9" rx="1.5" /><path d="M2 5l6 4 6-4" /></svg>
      );
    default:
      return null;
  }
}

export default function LandingPage() {
  const [showAuth, setShowAuth] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const accessToken = useStore((s) => s.accessToken);
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const uiLang = useStore((s) => s.uiLang);
  const setUiLang = useStore((s) => s.setUiLang);
  const navigate = useNavigate();
  const t = useT();



  const handleGetStarted = () => {
    if (accessToken) navigate('/home');
    else setShowAuth(true);
  };

  const scrollTo = (id) => {
    setMobileMenu(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="landing">
      {showAuth && (
        <AuthModal
          onSuccess={() => { setShowAuth(false); navigate('/home'); }}
          onCancel={() => setShowAuth(false)}
        />
      )}

      {/* ===== NAV ===== */}
      <nav className="landing-nav">
        <Logo size={28} />

        <div className="landing-nav-links">
          <button className="nav-link" onClick={() => scrollTo('how-it-works')}>{t('nav.howItWorks')}</button>
          <button className="nav-link" onClick={() => scrollTo('features')}>{t('nav.features')}</button>
          <button className="nav-link" onClick={() => scrollTo('pricing')}>{t('nav.pricing')}</button>
          <button className="nav-link" onClick={() => scrollTo('who')}>{t('nav.whoFor')}</button>
          <button className="nav-link" onClick={() => scrollTo('contact')}>{t('nav.contact')}</button>
        </div>

        <div className="landing-nav-actions">
          <div className="lang-switcher">
            {['EN', 'UZ', 'RU'].map((lang) => (
              <button
                key={lang}
                className={`lang-btn${uiLang === lang.toLowerCase() ? ' active' : ''}`}
                onClick={() => setUiLang(lang.toLowerCase())}
              >{lang}</button>
            ))}
          </div>
          <button className="menu-icon-btn" onClick={toggleTheme} title="Toggle theme">
            {theme === 'light' ? '\u263E' : '\u2600'}
          </button>
          {accessToken ? (
            <button className="landing-btn-primary" style={{ marginLeft: 8 }} onClick={() => navigate('/home')}>
              {t('landing.goToDashboard')}
            </button>
          ) : (
            <>
              <button className="landing-btn-primary" style={{ marginLeft: 8 }} onClick={handleGetStarted}>
                {t('landing.getStarted')}
              </button>
            </>
          )}

          <button className="mobile-menu-btn" onClick={() => setMobileMenu(!mobileMenu)}>
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              {mobileMenu ? <path d="M6 6l10 10M16 6L6 16" /> : <path d="M4 6h14M4 11h14M4 16h14" />}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      {mobileMenu && (
        <div className="mobile-nav-dropdown">
          <button className="mobile-nav-link" onClick={() => scrollTo('how-it-works')}>{t('nav.howItWorks')}</button>
          <button className="mobile-nav-link" onClick={() => scrollTo('features')}>{t('nav.features')}</button>
          <button className="mobile-nav-link" onClick={() => scrollTo('pricing')}>{t('nav.pricing')}</button>
          <button className="mobile-nav-link" onClick={() => scrollTo('who')}>{t('nav.whoFor')}</button>
          <button className="mobile-nav-link" onClick={() => scrollTo('contact')}>{t('nav.contact')}</button>
          <div className="mobile-nav-divider" />
          <div className="mobile-nav-row">
            <div className="lang-switcher">
              {['EN', 'UZ', 'RU'].map((lang) => (
                <button
                  key={lang}
                  className={`lang-btn${uiLang === lang.toLowerCase() ? ' active' : ''}`}
                  onClick={() => setUiLang(lang.toLowerCase())}
                >{lang}</button>
              ))}
            </div>
            <button className="menu-icon-btn" onClick={toggleTheme} title="Toggle theme">
              {theme === 'light' ? '☾' : '☀'}
            </button>
          </div>
          <div className="mobile-nav-divider" />
          {accessToken ? (
            <button className="mobile-nav-link accent" onClick={() => { setMobileMenu(false); navigate('/home'); }}>
              {t('landing.goToDashboard')}
            </button>
          ) : (
            <button className="mobile-nav-link accent" onClick={() => { setMobileMenu(false); handleGetStarted(); }}>
              {t('landing.getStarted')}
            </button>
          )}
        </div>
      )}

      {/* ===== HERO (two-col: text + feature card) ===== */}
      <section className="landing-hero hero-split">
        <div className="hero-bg-glow" aria-hidden="true" />

        <div className="hero-text">
          <div className="hero-badge">{t('landing.badge')}</div>
          <h1 className="hero-title">
            {t('landing.title1')}<br />
            <span className="hero-highlight">{t('landing.title2')}</span>
          </h1>
          <p className="hero-desc">{t('landing.desc')}</p>
          <div className="hero-actions">
            <button className="hero-btn-primary" onClick={handleGetStarted}>
              {t('landing.start')}
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M4 9h10M10 5l4 4-4 4" />
              </svg>
            </button>
            <button className="hero-btn-secondary" onClick={() => navigate('/templates')}>
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2.5" y="2.5" width="13" height="13" rx="1.5" />
                <path d="M2.5 7h13M7 7v8.5" />
              </svg>
              {t('landing.browseTemplates')}
            </button>
          </div>
        </div>

        <div className="hero-features">
          <div className="hero-features-card">
            <div className="hero-features-tag">
              <span className="hero-features-dot" />
              {t('hero.features.tag')}
            </div>
            <ul className="hero-features-list">
              {[
                { key: 'hero.feat1', icon: 'rocket' },
                { key: 'hero.feat2', icon: 'lock' },
                { key: 'hero.feat3', icon: 'globe' },
                { key: 'hero.feat4', icon: 'database' },
                { key: 'hero.feat5', icon: 'backup' },
                { key: 'hero.feat6', icon: 'pulse' },
                { key: 'hero.feat7', icon: 'sync' },
                { key: 'hero.feat8', icon: 'mail' },
              ].map((f) => (
                <li key={f.key} className="hero-feat-item">
                  <span className={`hero-feat-icon icon-${f.icon}`}>
                    <FeatureIcon name={f.icon} />
                  </span>
                  <span className="hero-feat-text">{t(f.key)}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="landing-section" id="how-it-works">
        <div className="section-header">
          <span className="section-badge">{t('nav.howItWorks')}</span>
          <h2 className="section-title">{t('how.title')}</h2>
          <p className="section-desc">{t('how.desc')}</p>
        </div>

        <div className="how-steps">
          <div className="how-step">
            <div className="how-step-num">1</div>
            <div className="how-step-visual">
              <svg width="200" height="140" viewBox="0 0 200 140" fill="none">
                {/* Table 1 */}
                <rect x="10" y="20" width="70" height="60" rx="6" stroke="var(--accent)" strokeWidth="2" fill="rgba(74,144,217,0.06)" />
                <rect x="10" y="20" width="70" height="18" rx="6" fill="var(--accent)" opacity="0.15" />
                <text x="45" y="33" textAnchor="middle" fill="var(--accent)" fontSize="10" fontWeight="700">User</text>
                <text x="20" y="50" fill="var(--text-secondary)" fontSize="8">id</text>
                <text x="20" y="62" fill="var(--text-secondary)" fontSize="8">name</text>
                <text x="20" y="74" fill="var(--text-secondary)" fontSize="8">email</text>
                {/* Table 2 */}
                <rect x="120" y="30" width="70" height="50" rx="6" stroke="#f0ad4e" strokeWidth="2" fill="rgba(240,173,78,0.06)" />
                <rect x="120" y="30" width="70" height="18" rx="6" fill="#f0ad4e" opacity="0.15" />
                <text x="155" y="43" textAnchor="middle" fill="#f0ad4e" fontSize="10" fontWeight="700">Role</text>
                <text x="130" y="60" fill="var(--text-secondary)" fontSize="8">id</text>
                <text x="130" y="72" fill="var(--text-secondary)" fontSize="8">name</text>
                {/* Connection line */}
                <path d="M80 55 C100 55, 100 55, 120 55" stroke="var(--text-secondary)" strokeWidth="1.5" strokeDasharray="4 2" />
                <circle cx="80" cy="55" r="3" fill="var(--accent)" />
                <circle cx="120" cy="55" r="3" fill="#f0ad4e" />
              </svg>
            </div>
            <h3>{t('how.step1.title')}</h3>
            <p>{t('how.step1.desc')}</p>
          </div>

          <div className="how-step-arrow">
            <svg width="40" height="24" viewBox="0 0 40 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round">
              <path d="M4 12h28M26 6l6 6-6 6" />
            </svg>
          </div>

          <div className="how-step">
            <div className="how-step-num">2</div>
            <div className="how-step-visual">
              <svg width="200" height="140" viewBox="0 0 200 140" fill="none">
                {/* Config panel */}
                <rect x="20" y="15" width="160" height="110" rx="10" stroke="var(--border-color)" strokeWidth="1.5" fill="var(--bg-secondary)" />
                <rect x="30" y="30" width="60" height="10" rx="3" fill="var(--accent)" opacity="0.2" />
                <rect x="100" y="30" width="70" height="10" rx="3" fill="var(--border-color)" />
                <text x="38" y="38" fill="var(--accent)" fontSize="7" fontWeight="600">Stack</text>
                <rect x="30" y="50" width="140" height="8" rx="3" fill="var(--border-color)" />
                <rect x="30" y="50" width="80" height="8" rx="3" fill="var(--accent)" opacity="0.15" />
                <rect x="30" y="66" width="140" height="8" rx="3" fill="var(--border-color)" />
                {/* Checkboxes */}
                <rect x="30" y="84" width="10" height="10" rx="2" stroke="var(--accent)" strokeWidth="1.5" fill="var(--accent)" opacity="0.2" />
                <text x="44" y="92" fill="var(--text-secondary)" fontSize="7">Auth</text>
                <rect x="68" y="84" width="10" height="10" rx="2" stroke="var(--accent)" strokeWidth="1.5" fill="var(--accent)" opacity="0.2" />
                <text x="82" y="92" fill="var(--text-secondary)" fontSize="7">Validation</text>
                <rect x="118" y="84" width="10" height="10" rx="2" stroke="var(--accent)" strokeWidth="1.5" />
                <text x="132" y="92" fill="var(--text-secondary)" fontSize="7">Docs</text>
                {/* Button */}
                <rect x="60" y="104" width="80" height="14" rx="5" fill="var(--accent)" />
                <text x="100" y="114" textAnchor="middle" fill="#fff" fontSize="8" fontWeight="600">Generate</text>
              </svg>
            </div>
            <h3>{t('how.step2.title')}</h3>
            <p>{t('how.step2.desc')}</p>
          </div>

          <div className="how-step-arrow">
            <svg width="40" height="24" viewBox="0 0 40 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round">
              <path d="M4 12h28M26 6l6 6-6 6" />
            </svg>
          </div>

          <div className="how-step">
            <div className="how-step-num">3</div>
            <div className="how-step-visual">
              <svg width="200" height="140" viewBox="0 0 200 140" fill="none">
                {/* ZIP file (left) */}
                <rect x="10" y="20" width="70" height="80" rx="8" stroke="#2ea043" strokeWidth="2" fill="rgba(46,160,67,0.08)" />
                <path d="M22 38h46M22 48h46M22 58h46M22 68h46" stroke="#2ea043" strokeWidth="1" opacity="0.3" />
                <text x="45" y="88" textAnchor="middle" fill="#2ea043" fontSize="13" fontWeight="800">.ZIP</text>
                <text x="45" y="118" textAnchor="middle" fill="var(--text-secondary)" fontSize="9" fontWeight="600">Download</text>

                {/* OR text in middle */}
                <text x="100" y="68" textAnchor="middle" fill="var(--text-secondary)" fontSize="11" fontWeight="700">or</text>

                {/* Cloud / Deploy (right) */}
                <rect x="120" y="20" width="70" height="80" rx="8" stroke="var(--accent)" strokeWidth="2" fill="rgba(74,144,217,0.08)" />
                {/* Cloud shape */}
                <path d="M138 55 Q138 47, 146 47 Q146 40, 156 40 Q166 40, 168 47 Q176 47, 176 55 Q176 62, 168 62 L146 62 Q138 62, 138 55 z" fill="rgba(74,144,217,0.2)" stroke="var(--accent)" strokeWidth="1.5" />
                {/* Lightning bolt */}
                <path d="M154 68 L160 68 L156 78 L162 78 L152 92 L156 82 L150 82 z" fill="var(--accent)" />
                <text x="155" y="118" textAnchor="middle" fill="var(--text-secondary)" fontSize="9" fontWeight="600">Deploy</text>

                {/* Sparkles */}
                <circle cx="5" cy="30" r="2" fill="#2ea043" opacity="0.4" />
                <circle cx="195" cy="28" r="3" fill="var(--accent)" opacity="0.4" />
                <circle cx="100" cy="20" r="1.8" fill="var(--text-secondary)" opacity="0.5" />
                <circle cx="100" cy="100" r="1.8" fill="var(--text-secondary)" opacity="0.4" />
              </svg>
            </div>
            <h3>{t('how.step3.title')}</h3>
            <p>{t('how.step3.desc')}</p>
          </div>
        </div>
      </section>

      {/* ===== FEATURES ===== */}
      <section className="landing-section alt-bg" id="features">
        <div className="section-header">
          <span className="section-badge">{t('nav.features')}</span>
          <h2 className="section-title">{t('features.title')}</h2>
        </div>

        <div className="features-grid">
          {[
            { icon: '1', title: t('landing.feature1.title'), desc: t('landing.feature1.desc'), color: 'var(--accent)' },
            { icon: '2', title: t('landing.feature2.title'), desc: t('landing.feature2.desc'), color: '#2ea043' },
            { icon: '3', title: t('landing.feature3.title'), desc: t('landing.feature3.desc'), color: '#7c3aed' },
            { icon: '4', title: t('features.autoSave.title'), desc: t('features.autoSave.desc'), color: '#f0ad4e' },
            { icon: '5', title: t('features.export.title'), desc: t('features.export.desc'), color: '#e44d90' },
            { icon: '6', title: t('features.darkMode.title'), desc: t('features.darkMode.desc'), color: '#6366f1' },
          ].map((f, i) => (
            <div key={i} className="feature-card-v2">
              <div className="feature-card-icon" style={{ background: `${f.color}15`, color: f.color }}>
                <span>{f.icon}</span>
              </div>
              <h3>{f.title}</h3>
              <p>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="landing-section" id="pricing">
        <div className="section-header">
          <span className="section-badge">{t('nav.pricing')}</span>
          <h2 className="section-title">{t('pricing.title')}</h2>
          <p className="section-desc">{t('pricing.desc')}</p>
        </div>

        <div className="pricing-grid">
          {/* Free — left */}
          <div className="pricing-card">
            <div className="pricing-card-header">
              <h3 className="pricing-plan-name">{t('pricing.freePlan')}</h3>
              <div className="pricing-price">
                <span className="pricing-amount">{t('pricing.freePrice')}</span>
              </div>
              <p className="pricing-plan-desc">{t('pricing.freeDesc')}</p>
            </div>
            <ul className="pricing-features">
              <li>{t('pricing.free1')}</li>
              <li>{t('pricing.free2')}</li>
              <li>{t('pricing.free3')}</li>
              <li>{t('pricing.free4')}</li>
              <li>{t('pricing.free5')}</li>
            </ul>
            <button className="pricing-btn" onClick={handleGetStarted}>
              {t('pricing.getStarted')}
            </button>
          </div>

          {/* Hobby — center, popular (highlighted) */}
          <div className="pricing-card popular">
            <div className="pricing-popular-badge">{t('pricing.popular')}</div>
            <div className="pricing-card-header">
              <h3 className="pricing-plan-name">{t('pricing.hobbyPlan')}</h3>
              <div className="pricing-price">
                <span className="pricing-amount">{t('pricing.hobbyPrice')}</span>
                <span className="pricing-period">{t('pricing.perProject')}</span>
              </div>
              <p className="pricing-plan-desc">{t('pricing.hobbyDesc')}</p>
            </div>
            <ul className="pricing-features">
              <li>{t('pricing.hobby1')}</li>
              <li>{t('pricing.hobby2')}</li>
              <li>{t('pricing.hobby3')}</li>
              <li>{t('pricing.hobby4')}</li>
              <li>{t('pricing.hobby5')}</li>
            </ul>
            <button className="pricing-btn pro" onClick={handleGetStarted}>
              {t('pricing.getStarted')}
            </button>
          </div>

          {/* Pro — right */}
          <div className="pricing-card">
            <div className="pricing-card-header">
              <h3 className="pricing-plan-name">{t('pricing.builderPlan')}</h3>
              <div className="pricing-price">
                <span className="pricing-amount">{t('pricing.builderPrice')}</span>
                <span className="pricing-period">{t('pricing.perProject')}</span>
              </div>
              <p className="pricing-plan-desc">{t('pricing.builderDesc')}</p>
            </div>
            <ul className="pricing-features">
              <li className="pricing-feature-includes">{t('pricing.builder1')}</li>
              <li>{t('pricing.builder2')}</li>
              <li>{t('pricing.builder3')}</li>
              <li>{t('pricing.builder4')}</li>
              <li>{t('pricing.builder5')}</li>
            </ul>
            <button className="pricing-btn" onClick={handleGetStarted}>
              {t('pricing.getStarted')}
            </button>
          </div>
        </div>

        <div className="comparison-block">
          <div className="comparison-header">
            <h3>{t('comparison.title')}</h3>
            <p>{t('comparison.desc')}</p>
          </div>

          <div className="comparison-grid">
            <div className="comparison-card backora">
              <div className="comparison-card-header">
                <h4>{t('comparison.backoraTitle')}</h4>
                <p>{t('comparison.backoraSubtitle')}</p>
              </div>
              <ul className="comparison-list">
                <li>{t('comparison.b1')}</li>
                <li>{t('comparison.b2')}</li>
                <li>{t('comparison.b3')}</li>
                <li>{t('comparison.b4')}</li>
                <li>{t('comparison.b5')}</li>
                <li>{t('comparison.b6')}</li>
                <li>{t('comparison.b7')}</li>
                <li>{t('comparison.b8')}</li>
              </ul>
            </div>

            <div className="comparison-card diy">
              <div className="comparison-card-header">
                <h4>{t('comparison.diyTitle')}</h4>
                <p>{t('comparison.diySubtitle')}</p>
              </div>
              <ul className="comparison-list">
                <li>{t('comparison.d1')}</li>
                <li>{t('comparison.d2')}</li>
                <li>{t('comparison.d3')}</li>
                <li>{t('comparison.d4')}</li>
                <li>{t('comparison.d5')}</li>
                <li>{t('comparison.d6')}</li>
                <li>{t('comparison.d7')}</li>
                <li>{t('comparison.d8')}</li>
              </ul>
            </div>
          </div>

          <p className="comparison-cta">{t('comparison.cta')}</p>
        </div>

        <p className="pricing-note">{t('pricing.betaNote')}</p>
      </section>

      {/* ===== WHO IS IT FOR ===== */}
      <section className="landing-section" id="who">
        <div className="section-header">
          <span className="section-badge">{t('nav.whoFor')}</span>
          <h2 className="section-title">{t('who.title')}</h2>
        </div>

        <div className="who-grid">
          {[
            { emoji: '&#128104;&#8205;&#128187;', title: t('who.dev.title'), desc: t('who.dev.desc') },
            { emoji: '&#127891;', title: t('who.student.title'), desc: t('who.student.desc') },
            { emoji: '&#128640;', title: t('who.startup.title'), desc: t('who.startup.desc') },
            { emoji: '&#128101;', title: t('who.team.title'), desc: t('who.team.desc') },
          ].map((w, i) => (
            <div key={i} className="who-card">
              <span className="who-emoji" dangerouslySetInnerHTML={{ __html: w.emoji }} />
              <h3>{w.title}</h3>
              <p>{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ===== CONTACT ===== */}
      <section className="landing-section alt-bg" id="contact">
        <div className="section-header">
          <span className="section-badge">{t('nav.contact')}</span>
          <h2 className="section-title">{t('contact.title')}</h2>
          <p className="section-desc">{t('contact.desc')}</p>
        </div>

        <div className="contact-cards">
          <a href="mailto:davron.sharipovv@gmail.com" className="contact-card">
            <div className="contact-icon email-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="2" y="4" width="20" height="16" rx="3" />
                <path d="M2 7l10 7 10-7" />
              </svg>
            </div>
            <span className="contact-label">Email</span>
            <span className="contact-value">davron.sharipovv@gmail.com</span>
          </a>

          <a href="https://t.me/Davronbek_Sharipov" target="_blank" rel="noopener noreferrer" className="contact-card">
            <div className="contact-icon tg-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69.01-.03.01-.14-.07-.2-.08-.06-.19-.04-.27-.02-.12.02-1.96 1.25-5.54 3.66-.52.36-1 .53-1.42.52-.47-.01-1.37-.26-2.03-.48-.82-.27-1.47-.42-1.42-.88.03-.24.37-.49 1.02-.75 3.99-1.74 6.65-2.89 7.99-3.45 3.8-1.6 4.59-1.88 5.1-1.89.11 0 .37.03.54.17.14.12.18.28.2.45-.01.06-.01.24-.02.38z" />
              </svg>
            </div>
            <span className="contact-label">Telegram</span>
            <span className="contact-value">@Davronbek_Sharipov</span>
          </a>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="landing-cta">
        <h2>{t('cta.title')}</h2>
        <p>{t('cta.desc')}</p>
        <button className="hero-btn-primary" onClick={handleGetStarted}>
          {t('landing.start')}
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M4 9h10M10 5l4 4-4 4" />
          </svg>
        </button>
      </section>

      {/* Scroll to top */}
      <button
        className={`scroll-top-btn ${showScrollTop ? 'visible' : ''}`}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        title="Back to top"
      >
        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
          <path d="M9 14V4M5 8l4-4 4 4" />
        </svg>
      </button>

      {/* ===== FOOTER ===== */}
      <footer className="landing-footer-v2">
        <div className="footer-content">
          <div className="footer-brand">
            <Logo size={22} />
            <p>{t('landing.footer')}</p>
          </div>
          <div className="footer-links">
            <h4>{t('footer.nav')}</h4>
            <button onClick={() => scrollTo('how-it-works')}>{t('nav.howItWorks')}</button>
            <button onClick={() => scrollTo('features')}>{t('nav.features')}</button>
            <button onClick={() => scrollTo('pricing')}>{t('nav.pricing')}</button>
            <button onClick={() => scrollTo('who')}>{t('nav.whoFor')}</button>
            <button onClick={() => scrollTo('contact')}>{t('nav.contact')}</button>
          </div>
          <div className="footer-links">
            <h4>{t('footer.connect')}</h4>
            <a href="mailto:davron.sharipovv@gmail.com">Email</a>
            <a href="https://t.me/Davronbek_Sharipov" target="_blank" rel="noopener noreferrer">Telegram</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2026 Backora. {t('footer.rights')}</p>
        </div>
      </footer>
    </div>
  );
}
